import { Injectable } from '@angular/core';
import writeXlsxFile, { type SheetData } from 'write-excel-file/browser';
import { CostColumnsSnapshot } from '#models';
import { SnapshotPurpose } from '#enums';

export interface SnapshotExportRow {
    snapshotDate: string;
    purpose: SnapshotPurpose;
    remediationId?: string;
    remediationName?: string;
    costColumns: CostColumnsSnapshot;
}

/** One row per Year within a snapshot, wide month format — shared by the portfolio and
 * per-remediation Snapshot Excel exports. */
@Injectable({ providedIn: 'root' })
export class SnapshotExportService {
    buildSheetData(rows: SnapshotExportRow[], includeRemediationColumns: boolean): SheetData {
        const header = [
            'Snapshot Date', 'Purpose',
            ...(includeRemediationColumns ? ['Remediation ID', 'Remediation Name'] : []),
            'Original Budget (A)', 'Approved Performance Budget Adj (B)', 'Performance Budget (C)',
            'Approved Current Budget Adj (D)', 'Current Budget (E)', 'Pending Adjustments (F)', 'Forecast (G)',
            'ITD VOWD', 'ITD Booked Actuals', 'Calculated Estimate Accrual',
            'Year', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Year Total',
        ];

        const dataRows: (string | number)[][] = [];

        for (const row of rows) {
            const baseCells: (string | number)[] = [
                row.snapshotDate,
                row.purpose,
                ...(includeRemediationColumns ? [row.remediationId ?? '', row.remediationName ?? ''] : []),
                row.costColumns.columnA, row.costColumns.columnB, row.costColumns.columnC,
                row.costColumns.columnD, row.costColumns.columnE, row.costColumns.columnF, row.costColumns.columnG,
                row.costColumns.itdVowd, row.costColumns.itdBookedActuals, row.costColumns.calculatedEstimateAccrual,
            ];

            const years = row.costColumns.monthlyEntries;
            if (!years || years.length === 0) {
                // Snapshot has no monthly entries — still emit its Column A-G/ITD summary.
                dataRows.push([...baseCells, '', '', '', '', '', '', '', '', '', '', '', '', '', '']);
                continue;
            }

            for (const year of years) {
                dataRows.push([
                    ...baseCells,
                    year.year,
                    year.jan, year.feb, year.mar, year.apr, year.may, year.jun,
                    year.jul, year.aug, year.sep, year.oct, year.nov, year.dec,
                    year.total,
                ]);
            }
        }

        return [header, ...dataRows] as SheetData;
    }

    async download(rows: SnapshotExportRow[], fileName: string, includeRemediationColumns: boolean): Promise<void> {
        const sheetData = this.buildSheetData(rows, includeRemediationColumns);
        await writeXlsxFile(sheetData, { sheet: 'Snapshots' }).toFile(fileName);
    }
}
